import React, { useState } from 'react';
import { useParams } from 'react-router';
import { Field } from '@atlaskit/form';
import Button, { LoadingButton } from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import { G500 } from '@atlaskit/theme/colors';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { validateApplicablePromocode } from '@css-xp/ppc-entitlement-page/src/ui/sidebar-panel/change-subscription-pane/queries/validate-promo-code';
import { CheckoutOperation, usePartnerCheckoutNotification } from '../hooks/use-place-order-notification';

export type PartnerQuoteCatalogueItemProps = {
  productId: string;
  removePromocode: (_productId: string) => void;
  managePromocodeDetails: any;
  isPromoCodeValid: boolean;
  hideRemovePromotionCTA?: boolean;
  hideSuccessIconOnValidPromotion?: boolean;
  showApplyFieldOnRemove?: boolean;
};
export const APP_PROMO_CODE_KEY_PREFIX = 'appPromoCode-';

const ManageAppsPromotionField: React.FC<PartnerQuoteCatalogueItemProps> = React.memo(
  ({
    productId,
    removePromocode,
    managePromocodeDetails,
    isPromoCodeValid,
    hideSuccessIconOnValidPromotion = false,
    hideRemovePromotionCTA = false,
    showApplyFieldOnRemove = false,
  }) => {
    const initialState = {
      isApplyButtonLoading: false,
      promoCodeNotApplicableErrors: null,
      promoCodeAPIError: '',
      validPromotionData: {},
    };
    const { notifyFailure } = usePartnerCheckoutNotification();
    const [isApplyButtonLoading, setIsApplyButtonLoading] = useState<boolean>(initialState.isApplyButtonLoading);
    const [promocode, setPromocode] = useState<string>('');
    const { txaId } = useParams<{ txaId: string }>();

    React.useEffect(() => {
      if (!promocode) {
        managePromocodeDetails(initialState);
      }
    }, [promocode]);

    const handleApplyPromoCode = async () => {
      setIsApplyButtonLoading(true);
      try {
        const { data, errors } = await graphqlClient().query({
          query: validateApplicablePromocode,
          variables: {
            id: '',
            promoCodes: [
              {
                code: promocode,
                productKey: productId,
              },
            ],
            transactionAccountId: txaId,
          },
          fetchPolicy: 'no-cache',
        });
        setIsApplyButtonLoading(false);

        if (errors) throw errors;

        if (data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason) {
          const promocodeWithNotApplicableError = {
            promoCodeNotApplicableErrors: data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason?.join(
              '.'
            ),
            validPromotionData: initialState.validPromotionData,
          };
          managePromocodeDetails(promocodeWithNotApplicableError);
        } else if (data?.entitlementDetails?.promoCodesApplicable?.promotionId) {
          const promoData = data?.entitlementDetails?.promoCodesApplicable?.promotions[0];
          const promocodeWithValidData = {
            promoCodeNotApplicableErrors: null,
            promoCodeAPIError: '',
            validPromotionData: { ...promoData, promocode: promocode },
          };
          managePromocodeDetails(promocodeWithValidData);
        }
      } catch (error) {
        notifyFailure(
          (error as Error)?.message ??
            'Something is not right at our end. Please try again. If the issue persists, please contact us.',
          CheckoutOperation.PromocodeValidation
        );
      } finally {
        setIsApplyButtonLoading(false);
      }
    };

    const renderRemoveButton = () => {
      if (hideRemovePromotionCTA) {
        return;
      }
      return (
        <Button
          appearance="link"
          spacing="none"
          testId={'remove-promo-code-' + productId}
          className="ml-16"
          onClick={() => {
            removePromocode(productId);
          }}
        >
          Remove
        </Button>
      );
    };

    return (
      <div className="manage-apps-promotions-container" data-testid="manage-apps-promotions">
        <Field name={`${APP_PROMO_CODE_KEY_PREFIX}.${productId}`} isRequired>
          {({ fieldProps }) => (
            <div className="manage-apps-promotions-input-search">
              <Textfield
                {...fieldProps}
                placeholder="+ Vendor promo"
                name={`${APP_PROMO_CODE_KEY_PREFIX}.${productId}`}
                testId={'promo-code-' + productId}
                className="promocode-input-field"
                onChange={(e: any) => {
                  setPromocode(e.target.value);
                  fieldProps.onChange(e);
                }}
                elemAfterInput={
                  !hideSuccessIconOnValidPromotion &&
                  isPromoCodeValid && <EditorSuccessIcon size="large" primaryColor={G500} label="" />
                }
                isDisabled={showApplyFieldOnRemove ? false : isApplyButtonLoading || isPromoCodeValid}
                value={promocode}
              />
            </div>
          )}
        </Field>
        {isPromoCodeValid && !showApplyFieldOnRemove ? (
          <>{renderRemoveButton()}</>
        ) : (
          <LoadingButton
            appearance="primary"
            isSelected
            isLoading={isApplyButtonLoading}
            isDisabled={isApplyButtonLoading || !promocode}
            testId={'apply-promo-code-' + productId}
            aria-label="Apply promocode"
            className="ml-16"
            onClick={() => {
              handleApplyPromoCode();
            }}
          >
            Apply
          </LoadingButton>
        )}
      </div>
    );
  }
);

ManageAppsPromotionField.displayName = 'ManageAppsPromotionField';
export default ManageAppsPromotionField;
